import {
  callSettingsSchema,
  effectivePreferences,
  notificationEvents,
  notificationSettingsSchema,
  transcriptionSettingsSchema,
  WHISPER_MODELS,
  type CallSettings as Settings,
  type EffectivePreferences,
  type NotificationSettings,
  type TranscriptionSettingsView,
} from '@smarton-pbx/shared';
import { Bell, FileText, PhoneCall } from 'lucide-react';
import { type FormEvent, type ReactNode, useState } from 'react';
import { api } from '../api';
import { TextListInput } from '../components/pickers';
import { Field, Loading, Notice, PageHeader } from '../components/ui';
import { language, locale, t, tp } from '../i18n';
import { useLoad, useSubmit } from '../lib/hooks';
import { AnalysisSettingsCard, CategoriesCard, FeedbackCard } from './AnalysisSettings';
import { PreferencesMatrix } from './NotificationPreferences';
import { RecordingNoticeCard } from './RecordingNotice';
import { SettingsTabs } from './SettingsTabs';

const languageNames = new Intl.DisplayNames([language], { type: 'language' });

function SectionTitle({ icon: Icon, children }: { icon: typeof Bell; children: ReactNode }) {
  return (
    <div className="card-header">
      <div className="row" style={{ gap: '0.6rem' }}>
        <Icon size={18} className="subtle" aria-hidden />
        <h2>{children}</h2>
      </div>
    </div>
  );
}

/** The tenant's settings: calls, notifications and transcription. Admins only. */
export function CallSettings() {
  const settings = useLoad(api.callSettings);
  const notifications = useLoad(api.notificationSettings);
  const transcription = useLoad(api.transcriptionSettings);
  return (
    <div className="stack-lg">
      <SettingsTabs />
      <PageHeader title={t('Settings')} description={t('How calls ring, who is told about what, and how calls are transcribed and reviewed.')} />
      {(settings.error ?? notifications.error ?? transcription.error) && <Notice kind="error">{settings.error ?? notifications.error ?? transcription.error}</Notice>}
      {!settings.data ? <Loading /> : <SettingsForm settings={settings.data} onSaved={settings.setData} />}
      {notifications.data && <NotificationDefaults settings={notifications.data} />}
      <RecordingNoticeCard />
      {transcription.data && <TranscriptionForm settings={transcription.data} onSaved={transcription.setData} />}
      <AnalysisSettingsCard />
      <CategoriesCard />
      <FeedbackCard />
    </div>
  );
}

function NotificationDefaults({ settings }: { settings: NotificationSettings }) {
  const [value, setValue] = useState<EffectivePreferences>(effectivePreferences(settings.defaults));
  const [pushShowsCaller, setPushShowsCaller] = useState(settings.pushShowsCaller);
  const [saved, setSaved] = useState(false);
  const form = useSubmit();

  async function save() {
    setSaved(false);
    const result = await form.submit(notificationSettingsSchema, { defaults: value, pushShowsCaller }, api.updateNotificationSettings);
    if (result) setSaved(true);
  }

  return (
    <div className="card">
      <SectionTitle icon={Bell}>{t('Notifications')}</SectionTitle>
      <div className="card-body stack">
        <p className="muted">{t('What people are told about unless they choose otherwise on their account page. Carrier and blocked-call alerts go to admins.')}</p>
        <PreferencesMatrix
          events={notificationEvents}
          value={value}
          onChange={(next) => {
            setSaved(false);
            setValue(next);
          }}
          testId="notification-defaults"
        />
        <label className="checkbox">
          <input type="checkbox" checked={pushShowsCaller} onChange={(e) => (setSaved(false), setPushShowsCaller(e.target.checked))} data-testid="push-shows-caller" />
          {t('Show the caller’s number in notifications on the computer')}
        </label>
        <p className="subtle" style={{ marginTop: '-0.6rem', fontSize: '0.85rem' }}>
          {t('These can be seen on a locked screen. Off: they only say “Missed call” or “Incoming call”.')}
        </p>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && <Notice kind="success">{t('Saved.')}</Notice>}
      </div>
      <div className="card-footer">
        <button type="button" onClick={() => void save()} disabled={form.busy} data-testid="save-notification-defaults">
          {t('Save')}
        </button>
      </div>
    </div>
  );
}

/** Models for every language: those tuned for one can't detect the language. */
const KNOWN_MODELS = WHISPER_MODELS.filter((m) => !m.language);

/** Models hosted services take that give the line-by-line timings transcripts need. */
const HOSTED_MODELS = ['whisper-1', 'whisper-large-v3', 'whisper-large-v3-turbo'];

function TranscriptionForm({ settings, onSaved }: { settings: TranscriptionSettingsView; onSaved: (s: TranscriptionSettingsView) => void }) {
  const [mode, setMode] = useState(settings.mode);
  const [vocabulary, setVocabulary] = useState(settings.vocabulary);
  const [models, setModels] = useState<Record<string, string>>(settings.models);
  // "" = the server's default; a known name; or another typed in.
  const [model, setModel] = useState(settings.model ?? '');
  const [otherModel, setOtherModel] = useState(Boolean(settings.model && !KNOWN_MODELS.some((m) => m.value === settings.model)));
  const [hostedUrl, setHostedUrl] = useState(settings.hostedUrl ?? '');
  const [hostedModel, setHostedModel] = useState(settings.hostedModel ?? '');
  const [hostedKey, setHostedKey] = useState('');
  const [trainingClipDays, setTrainingClipDays] = useState(String(settings.trainingClipDays));
  const [saved, setSaved] = useState(false);
  const form = useSubmit();
  const change = <T,>(set: (value: T) => void) => (value: T) => {
    setSaved(false);
    set(value);
  };

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const input = {
      mode,
      model: model.trim() || null,
      vocabulary,
      models: Object.fromEntries(Object.entries(models).filter(([, m]) => m.trim())),
      hostedUrl: hostedUrl.trim() || null,
      hostedModel: hostedModel.trim() || null,
      hostedKey: hostedKey || null,
      trainingClipDays: Number(trainingClipDays),
    };
    const result = await form.submit(transcriptionSettingsSchema, input, api.updateTranscriptionSettings);
    if (result) {
      onSaved(result);
      setHostedKey('');
      setSaved(true);
    }
  }

  return (
    <form className="card" onSubmit={submit} noValidate>
      <SectionTitle icon={FileText}>{t('Transcription')}</SectionTitle>
      <div className="card-body stack">
        <Field label={t('Transcribe calls')} error={form.fields.mode}>
          <select value={mode} onChange={(e) => change(setMode)(e.target.value as typeof mode)} data-testid="transcription-mode" style={{ maxWidth: '24rem' }}>
            <option value="LOCAL">{t('On this server (recordings stay here)')}</option>
            <option value="HOSTED">{t('With a hosted service')}</option>
            <option value="OFF">{t('Off')}</option>
          </select>
        </Field>
        {mode === 'HOSTED' && (
          <>
            <Notice>
              {t('Recordings are sent to this service to be transcribed, so they leave your server. It must offer an OpenAI-compatible transcription API.')}
            </Notice>
            <div className="form-grid">
              <Field label={t('Service address')} hint="OpenAI: https://api.openai.com/v1 · Groq: https://api.groq.com/openai/v1" error={form.fields.hostedUrl}>
                <input value={hostedUrl} onChange={(e) => change(setHostedUrl)(e.target.value)} spellCheck={false} />
              </Field>
              <Field label={t('Model')} hint={t('OpenAI: whisper-1. Groq: whisper-large-v3 or whisper-large-v3-turbo')} error={form.fields.hostedModel}>
                <input value={hostedModel} onChange={(e) => change(setHostedModel)(e.target.value)} spellCheck={false} list="hosted-whisper-models" />
                <datalist id="hosted-whisper-models">
                  {HOSTED_MODELS.map((m) => (
                    <option key={m} value={m} />
                  ))}
                </datalist>
              </Field>
              <Field label={t('API key')} hint={settings.hasHostedKey ? t('Leave empty to keep the current one') : t('Never shown again')} error={form.fields.hostedKey}>
                <input type="password" value={hostedKey} onChange={(e) => change(setHostedKey)(e.target.value)} autoComplete="new-password" placeholder={settings.hasHostedKey ? '••••••••' : ''} />
              </Field>
            </div>
          </>
        )}
        {mode !== 'OFF' && (
          <>
            <Field
              group
              label={t('Names and terms to spell right')}
              hint={t('Your brand, products, partners and local words. Passed to Whisper as a hint; models tuned for one language (like agirdi) get none, as a hint garbles what they write.')}
              error={form.fields.vocabulary}
            >
              <TextListInput value={vocabulary} onChange={change(setVocabulary)} placeholder={t('Type a name and press Enter')} label={t('Add a name or term')} testId="vocabulary" />
            </Field>
            {mode === 'LOCAL' && (
              <div className="form-grid">
                <Field label={t('Model')} hint={t('For every language; each transcript says which model made it. A new model downloads on first use.')} error={form.fields.model}>
                  <select
                    value={otherModel ? '__other__' : model}
                    onChange={(e) => {
                      const value = e.target.value;
                      setOtherModel(value === '__other__');
                      change(setModel)(value === '__other__' ? '' : value);
                    }}
                    data-testid="transcription-model"
                  >
                    <option value="">{t('Server default ({model})', { model: settings.defaultModel })}</option>
                    {KNOWN_MODELS.filter((m) => m.value !== settings.defaultModel).map((m) => (
                      <option key={m.value} value={m.value}>
                        {t(m.label)}
                      </option>
                    ))}
                    <option value="__other__">{t('Another model…')}</option>
                  </select>
                </Field>
                {otherModel && (
                  <Field label={t('Model name')} hint={t('A faster-whisper name, a Hugging Face repository in CTranslate2 format, or a folder in the server’s models (/models/…)')}>
                    <input value={model} onChange={(e) => change(setModel)(e.target.value)} spellCheck={false} placeholder={t('e.g. medium')} />
                  </Field>
                )}
              </div>
            )}
            <div className="stack" style={{ gap: '0.6rem' }}>
              <Field
                label={t('Keep corrected lines for training')}
                hint={t('Days. When people correct a transcript or mark it checked, its lines are kept with that bit of audio, to fine-tune the transcription model on. Usually longer than the recordings: say so in your privacy notice. 0 keeps none.')}
                error={form.fields.trainingClipDays}
              >
                <input
                  type="number"
                  min={0}
                  max={3650}
                  value={trainingClipDays}
                  onChange={(e) => change(setTrainingClipDays)(e.target.value)}
                  style={{ maxWidth: '8rem' }}
                  data-testid="training-clip-days"
                />
              </Field>
              <p className="subtle" style={{ fontSize: '0.85rem', margin: 0 }} data-testid="training-collected">
                {settings.training.lines === 0
                  ? t('Nothing kept yet.')
                  : t('Kept so far: {lines} {calls} ({minutes} min of speech), {corrected} of them corrected.', {
                      lines: tp(settings.training.lines, '{n} line', '{n} lines'),
                      calls: tp(settings.training.calls, 'from {n} call', 'from {n} calls'),
                      minutes: (settings.training.seconds / 60).toLocaleString(locale, { minimumFractionDigits: 1, maximumFractionDigits: 1 }),
                      corrected: settings.training.corrected,
                    })}
              </p>
            </div>
            {mode === 'LOCAL' && settings.languages.length > 0 && (
              <div className="stack" style={{ gap: '0.6rem' }}>
                <div>
                  <div style={{ fontWeight: 560 }}>{t('Model per language')}</div>
                  <div className="subtle" style={{ fontSize: '0.85rem' }}>
                    {t('Empty uses the model above. A Whisper model tuned for one language can be better: for Lithuanian, agirdi.')}
                  </div>
                </div>
                <div className="form-grid">
                  {settings.languages.map((code) => (
                    <Field key={code} label={languageNames.of(code) ?? code} error={form.fields[`models.${code}`]}>
                      <input
                        value={models[code] ?? ''}
                        placeholder={model || settings.defaultModel}
                        onChange={(e) => change(setModels)({ ...models, [code]: e.target.value })}
                        spellCheck={false}
                        list={`whisper-models-${code}`}
                        data-testid={`transcription-model-${code}`}
                      />
                      <datalist id={`whisper-models-${code}`}>
                        {/* Tuned for this language first. */}
                        {[...WHISPER_MODELS.filter((m) => m.language === code), ...KNOWN_MODELS].map((m) => (
                          <option key={m.value} value={m.value}>
                            {t(m.label)}
                          </option>
                        ))}
                      </datalist>
                    </Field>
                  ))}
                </div>
              </div>
            )}
          </>
        )}
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && <Notice kind="success">{t('Saved.')}</Notice>}
      </div>
      <div className="card-footer">
        <button type="submit" disabled={form.busy} data-testid="save-transcription">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}

function SettingsForm({ settings, onSaved }: { settings: Settings; onSaved: (settings: Settings) => void }) {
  const [confirmMobileCalls, setConfirm] = useState(settings.confirmMobileCalls);
  const [ringSeconds, setRing] = useState(String(settings.ringSeconds));
  const [mobileRingSeconds, setMobileRing] = useState(String(settings.mobileRingSeconds));
  const [wakeSeconds, setWake] = useState(String(settings.wakeSeconds));
  const [maxCallMinutes, setMax] = useState(String(settings.maxCallMinutes));
  const [userMaxConcurrentCalls, setUserMax] = useState(String(settings.userMaxConcurrentCalls));
  const [userDailyMinutes, setUserDaily] = useState(String(settings.userDailyMinutes));
  const [userDailyTexts, setUserTexts] = useState(String(settings.userDailyTexts));
  const [saved, setSaved] = useState(false);
  const form = useSubmit();

  async function submit(event: FormEvent) {
    event.preventDefault();
    setSaved(false);
    const input = {
      confirmMobileCalls,
      ringSeconds: Number(ringSeconds),
      mobileRingSeconds: Number(mobileRingSeconds),
      wakeSeconds: Number(wakeSeconds),
      maxCallMinutes: Number(maxCallMinutes),
      userMaxConcurrentCalls: Number(userMaxConcurrentCalls),
      userDailyMinutes: Number(userDailyMinutes),
      userDailyTexts: Number(userDailyTexts),
    };
    const result = await form.submit(callSettingsSchema, input, api.updateCallSettings);
    if (result) {
      onSaved(result);
      setSaved(true);
    }
  }

  const change = <T,>(set: (value: T) => void) => (value: T) => {
    setSaved(false);
    set(value);
  };

  return (
    <form className="card" onSubmit={submit} noValidate>
      <SectionTitle icon={PhoneCall}>{t('Calls')}</SectionTitle>
      <div className="card-body stack">
        <ol className="steps">
          <li>{t('A call to someone’s number rings their browser and app.')}</li>
          <li>{t('If they don’t answer, it rings their mobile, showing your number.')}</li>
          <li>
            {t(
              'If that isn’t answered either, the call goes where the person chose under “When I can’t answer” on their Account page: their voicemail (the default), a colleague or a queue. They can also be away for some days, or skipped while on a call.',
            )}
          </li>
        </ol>
        <div className="form-grid">
          <Field label={t('Ring browser and app')} hint={t('Seconds')} error={form.fields.ringSeconds}>
            <input inputMode="numeric" value={ringSeconds} onChange={(e) => change(setRing)(e.target.value)} data-testid="ring-seconds" />
          </Field>
          <Field label={t('Then ring the mobile')} hint={t('Seconds')} error={form.fields.mobileRingSeconds}>
            <input inputMode="numeric" value={mobileRingSeconds} onChange={(e) => change(setMobileRing)(e.target.value)} />
          </Field>
          <Field
            label={t('Wait for a closed app')}
            hint={t('Seconds. If nobody’s app is connected, the call waits this long for one to come online')}
            error={form.fields.wakeSeconds}
          >
            <input inputMode="numeric" value={wakeSeconds} onChange={(e) => change(setWake)(e.target.value)} />
          </Field>
          <Field label={t('Longest call')} hint={t('Minutes. Calls are ended after this, to limit runaway costs')} error={form.fields.maxCallMinutes}>
            <input inputMode="numeric" value={maxCallMinutes} onChange={(e) => change(setMax)(e.target.value)} />
          </Field>
          <Field label={t('Calls at once, per person')} hint={t('Calls out beyond this are refused')} error={form.fields.userMaxConcurrentCalls}>
            <input inputMode="numeric" value={userMaxConcurrentCalls} onChange={(e) => change(setUserMax)(e.target.value)} data-testid="user-max-calls" />
          </Field>
          <Field
            label={t('Minutes out per day, per person')}
            hint={t('Then calls out stop until tomorrow, and admins are told. The company’s own limits are set by your provider.')}
            error={form.fields.userDailyMinutes}
          >
            <input inputMode="numeric" value={userDailyMinutes} onChange={(e) => change(setUserDaily)(e.target.value)} data-testid="user-daily-minutes" />
          </Field>
          <Field label={t('Texts per day, per person')} hint={t('Then their texts stop until tomorrow')} error={form.fields.userDailyTexts}>
            <input inputMode="numeric" value={userDailyTexts} onChange={(e) => change(setUserTexts)(e.target.value)} data-testid="user-daily-texts" />
          </Field>
        </div>
        <label className="checkbox">
          <input type="checkbox" checked={confirmMobileCalls} onChange={(e) => change(setConfirm)(e.target.checked)} data-testid="confirm-mobile" />
          {t('Ask people to press 1 when they answer on their mobile')}
        </label>
        <p className="subtle" style={{ marginTop: '-0.6rem' }}>
          {t('Recommended. Without it, a mobile’s own voicemail can pick up the call, and the caller ends up there instead of yours.')}
        </p>
        {form.error && <Notice kind="error">{form.error}</Notice>}
        {saved && <Notice kind="success">{t('Saved.')}</Notice>}
      </div>
      <div className="card-footer">
        <button type="submit" disabled={form.busy} data-testid="save-call-settings">
          {t('Save')}
        </button>
      </div>
    </form>
  );
}
